<template>
  <div>
    <PageHeader :title="$t('PointsOfSale')">
      <CreateButton v-if="currentUserHasPermission('admin')" @click="createStore">
        {{ $t("AddStore") }}
      </CreateButton>
    </PageHeader>

    <IbanMissing v-if="isStoreInvoiceLevel && anyWithMissingIban" class="mb-4" />

    <SearchToolbar
      class="mb-6"
      :placeholder="'Recherche de point de vente par nom/url'"
      :initial-keywords="keywords"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      @update-keyword-search="updateKeywordSearch"
      @update-filter-search="updateFilterSearch"
    />
    <Table :columns="columns" :data="index" :is-row-clickable="true" @rowClick="redirectToStoreDetailsPage">
      <template #storeName="{ row }">
        <span v-if="isPresent(row.salesChannel)">{{ row.salesChannel }}</span>
        <span v-else-if="isPresent(row.setupBy)" class="text-gray-400">Configuré par : {{ row.setupBy }}</span>
      </template>
      <template #storeStatus="{ row }">
        <FieldSpan
          :style-to-add="getStoreStatusFieldDisplayStyle(storeStatusOrSetup(row))"
          :value="storeStatusOrSetup(row)"
        />
        <FieldSpan v-if="ibanMissing(row)" style-to-add="bg-red-100 text-black ml-4" value="Iban manquant" />
      </template>
      <template #actions="{ row }">
        <TrashIcon v-if="isStoreSetup(row)" class="h-5 cursor-pointer" @click.stop="openDisableStoreModal(row)" />
      </template>
    </Table>
    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="handleChangePage"
    />
    <DisableModal
      :open="disableStoreModalOpen"
      :storeId="disableStoreId"
      @closeDisableStoreModal="disableStoreModalOpen = false"
      @disableStore="disableStore"
    />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import DisableModal from "@/components/store/DisableModal.vue";
import IbanMissing from "@/components/IbanMissing.vue";
import { TrashIcon } from "@heroicons/vue/outline";
import { CreateButton, PageHeader, SearchToolbar, Table, Pagination, FieldSpan, isPresent } from "@estaly/ui";

export default {
  components: {
    CreateButton,
    PageHeader,
    SearchToolbar,
    Pagination,
    DisableModal,
    FieldSpan,
    IbanMissing,
    Table,
    TrashIcon,
  },

  data() {
    return {
      currentPage: 1,
      keywords: null,
      availableFilters: {
        status: [],
        iban: [],
      },
      selectedFilters: {
        status: [],
        iban: [],
      },
      disableStoreModalOpen: false,
      disableStoreId: null,
    };
  },

  computed: {
    ...mapState("store", ["isLoading", "totalCount", "totalPages", "index"]),
    ...mapGetters("store", [
      "getStatuses",
      "getIbanStatuses",
      "getStoreStatusFieldDisplayStyle",
      "isStoreActive",
      "isStoreSetup",
    ]),
    ...mapGetters("auth", ["currentUserHasPermission"]),

    anyWithMissingIban() {
      return this.index?.filter((store) => this.isStoreActive(store) && !store.iban).length > 0;
    },

    invoiceLevel() {
      return this.index?.[0]?.organization?.invoiceLevel;
    },

    isStoreInvoiceLevel() {
      return this.invoiceLevel === "store";
    },

    columns() {
      return [
        {
          field: "storeName",
          label: "Nom",
          slot: "storeName",
          bold: true,
        },
        {
          field: "storeStatus",
          label: "Statut",
          slot: "storeStatus",
        },
        {
          field: "actions",
          label: "",
          slot: "actions",
        },
      ];
    },

    status: {
      get() {
        return this.selectedFilters.status;
      },
      set(value) {
        if (!isPresent(value)) return (this.selectedFilters.status = []);

        this.selectedFilters.status = Array.isArray(value) ? value : [value];
      },
    },

    iban: {
      get() {
        return this.selectedFilters.iban;
      },
      set(value) {
        if (!isPresent(value)) return (this.selectedFilters.iban = []);

        this.selectedFilters.iban = Array.isArray(value) ? value : [value];
      },
    },

    searchParams() {
      return {
        ...this.$route.query,
        page: this.$route.query.page ? parseInt(this.$route.query.page, 10) : this.currentPage,
        status: isPresent(this.status) ? this.status : ["active", "setup"],
        iban: this.iban,
        includes: ["setup_by", "iban", "store_feature_configuration", "organization"],
      };
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("store", ["withLoader", "fetchIndex", "updateStoreStatus", "create"]),
    isPresent,

    async retrieveStores() {
      await this.withLoader(async () => {
        await this.fetchIndex(this.searchParams);
      });
    },

    updateRouteQueryParams() {
      let query = { tab: "stores" };
      if (this.currentPage) query.page = this.currentPage;
      if (this.keywords) query.keywords = this.keywords;
      if (this.status) query.status = this.status;
      if (this.iban) query.iban = this.iban;
      this.$router.push({ query });
    },

    loadQueryParams() {
      this.currentPage = this.$route.query.page ? parseInt(this.$route.query.page) : 1;
      this.keywords = this.$route.query.keywords || null;
      this.status = this.$route.query.status || [];
      this.iban = this.$route.query.iban || [];
    },

    handleChangePage(page) {
      this.currentPage = page;
      this.updateRouteQueryParams();
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.currentPage = 1;
      this.updateRouteQueryParams();
    },

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.currentPage = 1;
      this.updateRouteQueryParams();
    },

    async createStore() {
      await this.withLoader(async () => {
        const response = await this.create();
        if (response?.success) {
          this.notify({
            category: "simple",
            type: "success",
            title: "Nouveau point de vente créé",
            text: "Vous pouvez maintenant compléter les informations de votre point de vente",
          });

          this.redirectToStoreDetailsPage(response.data);
        }
        await this.retrieveStores(1, null);
      });
    },

    storeStatusOrSetup(store) {
      return store.setupStatus || store.status;
    },

    ibanMissing(store) {
      return !store.iban?.id && this.isStoreInvoiceLevel && this.isStoreActive(store);
    },

    redirectToStoreDetailsPage(store) {
      this.$router.push(`/admin/organization/stores/${store.id}`);
    },

    openDisableStoreModal(store) {
      this.disableStoreId = store.id;
      this.disableStoreModalOpen = true;
    },

    async disableStore(storeId) {
      this.disableStoreModalOpen = false;
      await this.withLoader(async () => {
        const response = await this.updateStoreStatus({ storeId, status: "inactive" });
        if (response?.success)
          await this.notify({ category: "simple", type: "success", title: "Mise à jour effectuée" });
        await this.retrieveStores(this.currentPage, this.keywords);
      });
    },
  },

  mounted() {
    this.availableFilters.status = this.getStatuses();
    this.availableFilters.iban = this.getIbanStatuses();
  },

  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.loadQueryParams();
        this.retrieveStores();
      },
    },
  },
};
</script>
